import React, { useState, useEffect, useRef } from 'react'
import { RiSendPlaneFill } from 'react-icons/ri'
import { FiActivity } from 'react-icons/fi'
import { HiOutlinePlus, HiOutlinePhotograph } from 'react-icons/hi'
import { Button, 
        Textarea, 
        Popover, PopoverTrigger, PopoverContent,
        Listbox, ListboxItem, useDisclosure
       } from "@heroui/react"
import { track } from '@vercel/analytics';

import { useChallenge } from '@/contexts/challenge-context'
import { useUser } from '@/contexts/user-context-v2'
import ActivityEdit from '@/components/activity-edit'
import UploadPanel from '@/components/upload-panel'

const FeedForm = ({ modalActive, onSave }) => {
  const { challenge } = useChallenge()
  const { user } = useUser()
  
  const [error, setError] = useState('')
  const [content, setContent] = useState('')
  const [images, setImages] = useState(new Map())
  const [toGroup, setToGroup] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [buttonState, setButtonState] = useState('disabled')
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false)
  const [activityFormType, setActivityFormType] = useState('modal')
  const lastOpenStateRef = useRef(false)
  
  // Create a custom modal disclosure with event listeners for the parent
  const modal = useDisclosure()
  const fileUploadRef = useRef()

  useEffect(() => {
    if (images.size > 0 || content.trim()) {
      setButtonState('enabled')
    } else {
      setButtonState('disabled')
    }
  }, [images, content])

  useEffect(() => {
    if (modal.isOpen === lastOpenStateRef.current) return
    lastOpenStateRef.current = modal.isOpen
    if (modal.isOpen) {
      modalActive.current = true
      document.documentElement.classList.add('overflow-hidden'); // <html>
      document.body.classList.add('overflow-hidden');
    } else {
      setTimeout(() => {
        modalActive.current = false
      }, 150)
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    }
  }, [modal.isOpen, modalActive]);

  const submit = async (e) => {
    e.preventDefault()
    if ((!content || !content?.trim()) && (!images || !images.size)) return
    
    setSaving(true)
    setError('')
    
    let imgs = []
    if (images.size > 0) {
      imgs = Array.from(images.values()).map(img => { 
        return img.key.replace('-originals', '')
      })
    }

    const b = {
      content,
      images: imgs
    }
    
    if (toGroup) {
      b.groupId = challenge.group.id // will get all challenge ids and post message to each
    } else {
      b.challengeId = challenge.id  
    }
    
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify(b)
      }
      const res = await fetch(`${process.env.API}/feeds`, postConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        if (res.status === 401) {
          setError('You are not authorized to post messages to this challenge.')
        } else {
          setError(errMsg)
        }
        track('feed-post-error', { challengeId: challenge?.id, images: b.images.length })
      }
      setContent('')
      setButtonState('disabled')
      setImages(new Map())
      const newItem = await res.json()
      onSave(newItem)
      track('feed-post-success', { challengeId: challenge?.id, images: b.images.length })
    } catch (err) {
      console.log(err)
      setError('Server error saving post.')
      track('feed-post-error', { challengeId: challenge?.id, images: b.images.length })
    }
    setSaving(false)
  }

  let isDisabled = false
  if (user.organizationId !== challenge.group.organizationId && !challenge.feedPosting) {
    isDisabled = true
  }

  return <div>
    <form name='form'
      role='form'
      noValidate
      onSubmit={submit} >

      <div className='flex items-end gap-1 px-4 py-2 bg-chTan-200' style={{ paddingBottom: isFocused ? '0.5rem' : `max(0.5rem, env(safe-area-inset-bottom, 0.5rem))` }} >
        <div className='flex-none max-h-[40px]'>
          <Popover placement='top-start' size='lg' isOpen={isAddPopoverOpen} onOpenChange={(open) => setIsAddPopoverOpen(open)}>
            <PopoverTrigger>
              <Button 
                isDisabled={isDisabled}
                className='bg-chTan-300'
                isIconOnly
                ><HiOutlinePlus className='text-xl text-chBlue-600' /></Button>
            </PopoverTrigger>
            <PopoverContent>
              <Listbox 
                aria-label="Main Menu" 
                itemClasses={{ base: 'text-chDarkBlue' }} >
                  <ListboxItem 
                    startContent={<FiActivity className='text-xl text-chTan-600' />}
                    onPress={() => {
                      // Set different form types based on screen size
                      const isMobileMedia = window.matchMedia('(max-width: 768px)').matches;
                      const isMobileCheck = window.innerWidth <= 768 && 'ontouchstart' in window;
                      setActivityFormType((isMobileMedia || isMobileCheck) ? 'panel' : 'modal')
                      modal.onOpen()
                      setIsAddPopoverOpen(false)  
                    }}
                    title='Log Activity' 
                    />
                  <ListboxItem 
                    startContent={<HiOutlinePhotograph className='text-xl text-chTan-600' />}
                    title='Upload Photo'
                    onPress={() => {
                      fileUploadRef.current.click()
                      setIsAddPopoverOpen(false)
                    }}
                    />
              </Listbox>
            </PopoverContent>
          </Popover>
        </div>
        <div className='grow'>
          <UploadPanel uploadRef={fileUploadRef} images={images} setImages={setImages} user={user} />
          
          {isDisabled ? 
            <div className='w-full bg-chTan-300 p-2 rounded-lg text-chTan-600'>Feed posting disabled.</div>
            
          : <Textarea
            id="feed-message-input"
            aria-label="Message input"
            aria-describedby="message-description"
            classNames={ {
              input: 'placeholder:text-chTan-900 text-md',
              inputWrapper: '!bg-chTan-300 border-1 data-[hover=true]:bg-chTan-300'
            } }
            minRows='1'
            maxRows={4}
            name='content'
            value={content}
            placeholder='Message...'
            isDisabled={saving}
            onFocus={(e) => {
              setIsFocused(true)
            }}
            onBlur={(e) => {
              setIsFocused(false)
            }}
            onChange={(e) => {
              const c = e.target.value
              if (c.length > 1000) {
                setError('Maximum of 1,000 characters.')
                return
              } else {
                setError('')
              }
              setContent(e.target.value)
            }} />}
        </div>
        {buttonState === 'enabled' && <div className='flex-none'>
          <Button
            isIconOnly
            className='h-[43px]'
            color='primary'
            type='submit'><RiSendPlaneFill className='text-white text-xl' /></Button>
        </div>}
        
      </div>
      
      <ActivityEdit type={activityFormType} modal={modal} onSave={(newItem) => {
        onSave(newItem)
        track(`activity-logged-ui`, { location: 'feed' })
        modal.onClose()
      }} />

      {/* <div className='flex items-start gap-2'>
        {(user.organizationId === challenge.group.organizationId) && <div className='grow'>
          <Checkbox size='sm' classNames={{ wrapper: 'bg-white items-center' }} onChange={() => { setToGroup(!toGroup) }}>
            Post to all {challenge.group.type === 'team' ? 'teams' : 'challenges'} in {challenge.group.name}
          </Checkbox>
        </div>}

        <div className='text-gray-400 text-sm pt-1 text-right flex-none'>{content.length}/1,000</div>
      </div> */}
    </form>
  </div>
}

export default FeedForm
